
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { ShieldAlert, ChevronRight, Check, LineChart, FileWarning, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const RiskManagementService = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Risk Management Services Through PEOs | The PEO App</title>
        <meta name="description" content="Mitigate business risks with comprehensive risk management services through a PEO partnership. Includes compliance management, workplace safety, and liability reduction." />
        <link rel="canonical" href="https://thepeoapp.com/services/risk-management" />
        <meta name="keywords" content="PEO risk management, business risk mitigation, workplace safety programs, compliance management, liability reduction, HR risk assessment, regulatory compliance" />
      </Helmet>
      
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-emerald-50 to-green-100 py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 mb-10 md:mb-0 md:pr-8">
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Comprehensive Risk Management Through PEOs</h1>
                <p className="text-xl text-gray-700 mb-8">Protect your business with expert risk management services that identify, assess, and mitigate potential threats across all operational areas.</p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" className="bg-[#00a86b] hover:bg-[#008f5a] text-white rounded-full">
                    <Link to="/application">Reduce Your Risk Today <ChevronRight className="ml-2 h-5 w-5" /></Link>
                  </Button>
                </div>
              </div>
              <div className="md:w-1/2">
                <div className="bg-white p-8 rounded-xl shadow-xl">
                  <div className="flex items-center mb-6">
                    <ShieldAlert className="h-10 w-10 text-[#00a86b] mr-4" />
                    <h2 className="text-2xl font-bold text-gray-900">PEO Risk Management Benefits</h2>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex">
                      <Check className="h-6 w-6 text-[#00a86b] mr-2 flex-shrink-0" />
                      <span className="text-gray-700">Proactive identification of potential business risks</span>
                    </li>
                    <li className="flex">
                      <Check className="h-6 w-6 text-[#00a86b] mr-2 flex-shrink-0" />
                      <span className="text-gray-700">Customized risk mitigation strategies for your industry</span>
                    </li>
                    <li className="flex">
                      <Check className="h-6 w-6 text-[#00a86b] mr-2 flex-shrink-0" />
                      <span className="text-gray-700">Compliance management across federal, state, and local regulations</span>
                    </li>
                    <li className="flex">
                      <Check className="h-6 w-6 text-[#00a86b] mr-2 flex-shrink-0" />
                      <span className="text-gray-700">Safety program development and implementation</span>
                    </li>
                    <li className="flex">
                      <Check className="h-6 w-6 text-[#00a86b] mr-2 flex-shrink-0" />
                      <span className="text-gray-700">Ongoing risk monitoring and management</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Key Services Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Comprehensive Risk Management Services</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-6 rounded-xl">
                <FileWarning className="h-12 w-12 text-[#00a86b] mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Compliance Management</h3>
                <p className="text-gray-700">Stay ahead of changing regulations with expert guidance on employment laws, workplace safety requirements, and industry-specific compliance needs.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl">
                <LineChart className="h-12 w-12 text-[#00a86b] mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Loss Prevention</h3>
                <p className="text-gray-700">Implement proven strategies to minimize workplace accidents, reduce workers' compensation claims, and create a culture of safety.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl">
                <HelpCircle className="h-12 w-12 text-[#00a86b] mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Crisis Management</h3>
                <p className="text-gray-700">Develop response plans for potential business disruptions, from natural disasters to workplace incidents, ensuring business continuity.</p>
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Frequently Asked Questions</h2>
            
            <div className="max-w-3xl mx-auto space-y-8">
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-3">How does a PEO help with risk management?</h3>
                <p className="text-gray-700">A PEO provides dedicated risk management experts who identify potential threats to your business, develop mitigation strategies, and implement safety programs—all while handling compliance requirements across multiple jurisdictions.</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Can risk management services be customized for my industry?</h3>
                <p className="text-gray-700">Yes, PEOs offer industry-specific risk management solutions tailored to your unique operational challenges, regulatory environment, and risk exposure levels.</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-3">How does risk management impact my insurance costs?</h3>
                <p className="text-gray-700">Effective risk management often leads to fewer claims and incidents, which can positively impact your experience modification rate (EMR) and potentially reduce insurance premiums over time.</p>
              </div>
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="bg-white py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Ready to Reduce Your Business Risk?</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              Our patent-pending platform connects you with vetted PEOs offering comprehensive risk management services tailored to your business needs.
            </p>
            <Button asChild size="lg" className="bg-[#00a86b] hover:bg-[#008f5a] text-white rounded-full px-8">
              <Link to="/application">Find Your Risk Management Solution</Link>
            </Button>
            <p className="text-sm text-gray-500 mt-4">One application, multiple quotes. No obligation.</p>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default RiskManagementService;
